import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    IconButton,
    Chip,
    Divider,
    Paper,
    Stack,
    Tooltip,
    Zoom,
    Slide,
    ButtonGroup,
} from '@mui/material';
import {
    Close as CloseIcon,
    ShoppingCart as CartIcon,
    Inventory as InventoryIcon,
    Category as CategoryIcon,
    QrCode as BarcodeIcon,
    Info as InfoIcon,
    AddShoppingCart as AddShoppingCartIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    QrCode2 as QrCodeIcon,
    ViewWeek as BarcodeWeekIcon,
    Download as DownloadIcon,
    Print as PrintIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import { enqueueSnackbar } from 'notistack';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';
import { useCart } from '../../context/CartContext';
import LabelPreview from './LabelPreview';
import { formatProductQrValue, formatProductCodeValue } from '../../utils/codeResolver';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const StockBadge = ({ stock, minStock = 5 }) => {
    if (stock <= 0)
        return (
            <Chip
                icon={<ErrorIcon fontSize="small" />}
                label="Sin stock"
                color="error"
                size="small"
                sx={{ fontWeight: 700 }}
            />
        );
    if (stock <= minStock)
        return (
            <Chip
                icon={<WarningIcon fontSize="small" />}
                label={`Stock bajo: ${stock}`}
                color="warning"
                size="small"
                sx={{ fontWeight: 700 }}
            />
        );
    return (
        <Chip
            icon={<CheckCircleIcon fontSize="small" />}
            label={`En stock: ${stock}`}
            color="success"
            size="small"
            sx={{ fontWeight: 700 }}
        />
    );
};

const DetailRow = ({ icon: Icon, label, value, color }) => {
    const theme = useTheme();
    if (!value && value !== 0) return null;
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                py: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
                '&:last-child': { borderBottom: 'none' },
            }}
        >
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette[color || 'primary'].main, 0.12),
                    flexShrink: 0,
                    mt: 0.3,
                }}
            >
                <Icon sx={{ fontSize: 18, color: `${color || 'primary'}.main` }} />
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, mb: 0.3 }}>
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );
};

/**
 * ProductDetailModal — Modal de detalle de producto con generador visual de QR,
 * código de barras, descarga directa e impresión de etiquetas RP425/térmicas.
 */
const ProductDetailModal = ({ open, onClose, product, onAddToCart }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { addProductToCart } = useCart();
    const [added, setAdded] = useState(false);
    const [codeTab, setCodeTab] = useState('qr'); // 'qr' | 'barcode'
    const [labelModalOpen, setLabelModalOpen] = useState(false);

    if (!product) return null;

    // Normalizar campos
    const name = product.nombre || product.name || 'Sin nombre';
    const price = product.precio ?? product.price ?? 0;
    const stock = product.stock_actual ?? product.stock ?? 0;
    const minStock = product.stock_minimo ?? product.minStock ?? 5;
    const code = product.codigo || product.code || '';
    const description = product.descripcion || product.description || '';
    const category = product.categoria || product.category || '';
    const brand = product.brand || product.marca || '';
    const provider = product.proveedor || product.provider || '';
    const imageUrl = product.imagen || product.imageUrl || (product.images && product.images[0]) || '';
    const barcode = product.barcode || product.codigo_barras || '';
    const id = product._id || product.id || '';

    // Código representativo garantizado (nunca vacío o 000000)
    const effectiveCode = barcode || code || (id ? `PRD-${String(id).slice(-8).toUpperCase()}` : 'PRD-0001');

    const handleAddToCart = () => {
        if (stock <= 0) {
            enqueueSnackbar('Producto sin stock disponible', { variant: 'warning' });
            return;
        }

        addProductToCart({
            id,
            name,
            price,
            stock,
            code: effectiveCode,
        });

        setAdded(true);
        setTimeout(() => setAdded(false), 2000);

        if (onAddToCart) onAddToCart(product);
    };

    const handleGoToSale = () => {
        handleAddToCart();
        onClose();
        navigate('/quick-sale');
    };

    // Descargar QR como imagen PNG de alta resolución
    const handleDownloadQr = () => {
        try {
            const svg = document.getElementById(`prod-qr-${id || 'detail'}`);
            if (!svg) {
                enqueueSnackbar('No se encontró el elemento QR', { variant: 'error' });
                return;
            }

            const svgData = new XMLSerializer().serializeToString(svg);
            const canvas = document.createElement('canvas');
            canvas.width = 400;
            canvas.height = 400;
            const ctx = canvas.getContext('2d');
            const img = new Image();

            img.onload = () => {
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, 400, 400);
                ctx.drawImage(img, 20, 20, 360, 360);
                const pngUrl = canvas.toDataURL('image/png');
                const a = document.createElement('a');
                a.download = `QR_${name.replace(/\s+/g, '_')}_${effectiveCode}.png`;
                a.href = pngUrl;
                a.click();
                enqueueSnackbar('Código QR descargado con éxito', { variant: 'success' });
            };

            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
        } catch (err) {
            console.error('Error descargando QR:', err);
            enqueueSnackbar('Error al descargar el código QR', { variant: 'error' });
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                TransitionComponent={Transition}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                    },
                }}
            >
                {/* Header con imagen */}
                <Box
                    sx={{
                        position: 'relative',
                        minHeight: 150,
                        background: imageUrl
                            ? `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 0)} 55%, ${theme.palette.background.paper} 100%), url(${imageUrl}) center/cover no-repeat`
                            : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                        display: 'flex',
                        alignItems: 'flex-end',
                        p: 2.5,
                    }}
                >
                    {!imageUrl && (
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0.15,
                            }}
                        >
                            <InventoryIcon sx={{ fontSize: 120 }} />
                        </Box>
                    )}

                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            bgcolor: alpha(theme.palette.background.paper, 0.8),
                            backdropFilter: 'blur(4px)',
                            '&:hover': { bgcolor: 'background.paper' },
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>

                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Chip
                            label={category || 'Sin categoría'}
                            size="small"
                            icon={<CategoryIcon fontSize="small" />}
                            sx={{
                                mb: 1,
                                bgcolor: alpha(theme.palette.background.paper, 0.9),
                                backdropFilter: 'blur(4px)',
                                fontWeight: 700,
                            }}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 800,
                                color: imageUrl ? 'text.primary' : 'white',
                                lineHeight: 1.2,
                            }}
                        >
                            {name}
                        </Typography>
                    </Box>
                </Box>

                <DialogContent sx={{ pt: 2, pb: 1 }}>
                    {/* Precio y stock */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
                        }}
                    >
                        <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                Precio de venta
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: 'primary.main', lineHeight: 1.1 }}>
                                RD$ {Number(price).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                        </Box>
                        <StockBadge stock={stock} minStock={minStock} />
                    </Box>

                    {/* SECCIÓN INTERACTIVA: CÓDIGO QR Y CÓDIGO DE BARRAS */}
                    <Paper
                        elevation={0}
                        sx={{
                            mb: 2.5,
                            p: 2,
                            borderRadius: 2.5,
                            bgcolor: alpha(theme.palette.background.default, 0.8),
                            border: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                    >
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <QrCodeIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                                <Typography variant="subtitle2" fontWeight="bold">
                                    Código del Producto (Escanear & Vender)
                                </Typography>
                            </Box>

                            <ButtonGroup size="small" variant="outlined">
                                <Button
                                    variant={codeTab === 'qr' ? 'contained' : 'outlined'}
                                    onClick={() => setCodeTab('qr')}
                                    startIcon={<QrCodeIcon sx={{ fontSize: 16 }} />}
                                    sx={{ py: 0.3, px: 1, textTransform: 'none', fontSize: '0.75rem', fontWeight: 700 }}
                                >
                                    QR
                                </Button>
                                <Button
                                    variant={codeTab === 'barcode' ? 'contained' : 'outlined'}
                                    onClick={() => setCodeTab('barcode')}
                                    startIcon={<BarcodeWeekIcon sx={{ fontSize: 16 }} />}
                                    sx={{ py: 0.3, px: 1, textTransform: 'none', fontSize: '0.75rem', fontWeight: 700 }}
                                >
                                    Barras
                                </Button>
                            </ButtonGroup>
                        </Box>

                        {/* Visualizador de código con fondo blanco puro para alto contraste */}
                        <Box
                            sx={{
                                width: '100%',
                                minHeight: 120,
                                bgcolor: '#ffffff',
                                borderRadius: 2,
                                p: 1.5,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                            }}
                        >
                            {codeTab === 'qr' ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                    {/* QR codifica POSENT:PROD:<code> para reconocimiento directo por el escáner de cámara del POS */}
                                    <QRCodeSVG
                                        id={`prod-qr-${id || 'detail'}`}
                                        value={formatProductQrValue(product)}
                                        size={105}
                                        level="Q"
                                        includeMargin={false}
                                    />
                                    <Typography sx={{ fontFamily: 'monospace', fontWeight: 900, color: '#000', fontSize: '0.85rem' }}>
                                        {effectiveCode}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', overflow: 'hidden' }}>
                                    <Barcode
                                        value={effectiveCode}
                                        width={1.6}
                                        height={45}
                                        fontSize={12}
                                        displayValue={true}
                                        background="#FFFFFF"
                                        margin={0}
                                    />
                                </Box>
                            )}
                        </Box>

                        {/* Botones de acción del código */}
                        <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, width: '100%' }}>
                            <Button
                                fullWidth
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadQr}
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                            >
                                Descargar QR
                            </Button>
                            <Button
                                fullWidth
                                size="small"
                                variant="contained"
                                color="secondary"
                                startIcon={<PrintIcon />}
                                onClick={() => setLabelModalOpen(true)}
                                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                            >
                                Imprimir Etiqueta
                            </Button>
                        </Stack>
                    </Paper>

                    {/* Descripción */}
                    {description && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Descripción
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary', lineHeight: 1.6 }}>
                                {description}
                            </Typography>
                        </Box>
                    )}

                    {/* Fila de detalles técnicos */}
                    <Box>
                        <DetailRow icon={BarcodeIcon} label="Código de sistema" value={code || effectiveCode} color="secondary" />
                        {barcode && <DetailRow icon={BarcodeIcon} label="Código de barras físico" value={barcode} color="secondary" />}
                        <DetailRow icon={InventoryIcon} label="Stock actual" value={`${stock} unidades`} color={stock <= 0 ? 'error' : stock <= minStock ? 'warning' : 'success'} />
                        <DetailRow icon={InventoryIcon} label="Stock mínimo" value={minStock ? `${minStock} unidades` : null} />
                        {brand && <DetailRow icon={InfoIcon} label="Marca" value={brand} color="info" />}
                        {provider && <DetailRow icon={InfoIcon} label="Proveedor" value={provider} color="info" />}
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        p: 2,
                        gap: 1,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        flexWrap: 'wrap',
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        sx={{ flex: '1 1 80px', minWidth: 80, borderRadius: 2 }}
                    >
                        Cerrar
                    </Button>

                    <Zoom in>
                        <Button
                            variant="contained"
                            color={added ? 'success' : 'secondary'}
                            startIcon={added ? <CheckCircleIcon /> : <AddShoppingCartIcon />}
                            onClick={handleAddToCart}
                            disabled={stock <= 0}
                            sx={{
                                flex: '2 1 140px',
                                minWidth: 140,
                                fontWeight: 700,
                                borderRadius: 2,
                                transition: 'all 0.3s',
                            }}
                        >
                            {added ? '¡Agregado!' : 'Agregar al carrito'}
                        </Button>
                    </Zoom>

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CartIcon />}
                        onClick={handleGoToSale}
                        disabled={stock <= 0}
                        sx={{ flex: '2 1 140px', minWidth: 140, fontWeight: 700, borderRadius: 2 }}
                    >
                        Ir a vender
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modal para imprimir etiqueta directamente desde el detalle */}
            {labelModalOpen && (
                <LabelPreview
                    open={labelModalOpen}
                    onClose={() => setLabelModalOpen(false)}
                    product={product}
                />
            )}
        </>
    );
};

export default ProductDetailModal;
